// ============================================================================
// CORPORATE SERVICE
// ============================================================================
// Business logic and API calls for Corporate module

import { get, post, put, del } from '../client/axiosClient';
import { corporateEndpoints } from '../endpoints/corporate.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  PaginatedResponse,
  EmptyResponse,
  ID,
  Corporate,
  CreateCorporateRequest,
  UpdateCorporateRequest,
  CorporateSearchRequest,
} from '../types';

/**
 * Corporate Service Class
 */
class CorporateService {
  /**
   * Get all corporates (paginated)
   */
  async getAll(params?: CorporateSearchRequest): Promise<PaginatedResponse<Corporate>> {
    const endpoint = params
      ? buildEndpointWithQuery('/Corporate', params)
      : corporateEndpoints.list();

    const response = await get<any>(endpoint);
    const raw = response.data;
    const pagedResult = raw.data ?? {};

    return {
      success: raw.success,
      message: raw.message,
      data: pagedResult.items ?? [],
      pagination: {
        currentPage: pagedResult.page ?? 1,
        pageSize: pagedResult.pageSize ?? 20,
        totalPages: pagedResult.totalPages ?? 0,
        totalItems: pagedResult.totalCount ?? 0,
        hasNextPage: pagedResult.hasNext ?? false,
        hasPreviousPage: pagedResult.hasPrevious ?? false,
      },
    };
  }

  /**
   * Get corporate by ID
   */
  async getById(id: ID): Promise<ApiResponse<Corporate>> {
    const response = await get<ApiResponse<Corporate>>(corporateEndpoints.getById(id));
    return response.data;
  }

  /**
   * Get corporate by code
   */
  async getByCode(code: string): Promise<ApiResponse<Corporate>> {
    const response = await get<ApiResponse<Corporate>>(corporateEndpoints.getByCode(code));
    return response.data;
  }

  /**
   * Create new corporate
   */
  async create(data: CreateCorporateRequest): Promise<ApiResponse<Corporate>> {
    const response = await post<ApiResponse<Corporate>>(
      corporateEndpoints.create(),
      data
    );
    return response.data;
  }

  /**
   * Update corporate
   */
  async update(id: ID, data: UpdateCorporateRequest): Promise<ApiResponse<Corporate>> {
    const response = await put<ApiResponse<Corporate>>(
      corporateEndpoints.update(id),
      data
    );
    return response.data;
  }

  /**
   * Delete corporate
   */
  async delete(id: ID, rowVersion: number): Promise<EmptyResponse> {
    const response = await del<EmptyResponse>(corporateEndpoints.delete(id), {
      params: { rowVersion },
    });
    return response.data;
  }

  /**
   * Validate corporate code uniqueness
   */
  async validateCode(code: string, excludeId?: ID): Promise<ApiResponse<boolean>> {
    let endpoint = corporateEndpoints.validateCode(code);
    if (excludeId) {
      endpoint = `${endpoint}?excludeId=${excludeId}`;
    }
    const response = await get<ApiResponse<boolean>>(endpoint);
    return response.data;
  }
}

/**
 * Export singleton instance
 */
export const corporateService = new CorporateService();
export default corporateService;
